/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Separator } from "../../../ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  ArrowLeft,
  User,
  Globe,
  FileText,
  Users,
  DollarSign,
  CheckCircle2,
  Package,
  Clock,
  AlertCircle,
  Download,
  FileStack,
  Shield,
  Baby,
  UserCircle,
  CreditCard,
  Plane,
  Eye,
  CheckCircle,
  XCircle,
  X,
  PauseCircle,
  Play,
  Ban,
  RefreshCw,
  MessageSquare,
  Edit2,
  Save,
  RotateCcw,
  Trash2,
  ChevronDown,
  ChevronUp,
  FileUser,
} from "lucide-react";
import { toast } from "sonner";
import { VisaRequestedDetailModel } from "../../../types/VisaRequestedDetailModel";
import { getFileViewUrl } from "../../../utils/commonutils";
import {
  updateVisaApplication,
  updateVisaProcessingStatus,
  UpdateVisaApplicationPayload,
  changeVisaApplicationStatus,
  changeVisaApplicationNameDiscrepancy,
  getVisaReviewConversation,
  sendVisaReviewMessage,
} from "../../../services/visaService";
import React from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useModule } from "../../../contexts/ModuleContext";
import { useScreenPermission } from "../../../hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";

interface VisaRequestedDetailViewProps {
  applicant: VisaRequestedDetailModel;
  onNavigate?: (page: string) => void;
  onBack?: () => void;
  isInBatch?: boolean;
}

interface ReviewMessage {
  id: string;
  sender: "backoffice" | "tour_operator";
  senderName: string;
  message: string;
  timestamp: string;
  dateTime: string;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export interface User {
  userId: number;
  username: string;
  fullName?: string;
  email: string;
}

type ApplicantType = "Adult" | "Child" | "Infant" | "Senior";

export const VisaRequestedDetailView = ({
  // applicant = mockApplicant,
  applicant,
  onNavigate,
  onBack,
  isInBatch,
}: VisaRequestedDetailViewProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setPageHeader } = usePageHeader();
  const isBatchApplication = applicant.batchId !== null;

  const [activeTab, setActiveTab] = useState<"details" | "documents" | "hold">(
    "details",
  );
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");

  const [displayData, setDisplayData] =
    useState<VisaRequestedDetailModel>(applicant);
  const isRefunded =
    displayData.currentStatusCode?.toLowerCase() === "refunded";
  const [savingHoldStatus, setSavingHoldStatus] = useState(false);

  // Hold Application states
  const [holdStatus, setHoldStatus] = useState<"process" | "hold" | "refunded">(
    isRefunded ? "refunded" : applicant.isApplicationHold ? "process" : "hold",
  );

  useEffect(() => {
    setHoldStatus(
      isRefunded
        ? "refunded"
        : displayData.isApplicationHold
          ? "process"
          : "hold",
    );
  }, [
    displayData.visaApplicationId,
    displayData.isApplicationHold,
    isRefunded,
  ]);
  const [holdRemark, setHoldRemark] = useState("");
  //   const [isHoldActive, setIsHoldActive] = useState(false);

  const saveHoldStatus = async () => {
    if (isRefunded || savingHoldStatus) return;
    if (holdRemark.trim().length < 10) {
      toast.error("Please provide a detailed remark (minimum 10 characters)");
      return;
    }
    try {
      setSavingHoldStatus(true);
      const response = await updateVisaProcessingStatus(
        applicant.visaApplicationId,
        {
          status: holdStatus,
          remark: holdRemark.trim(),
        },
      );
      if (response.success === false) {
        toast.error(response.message || "Failed to save hold status");
        return;
      }
      setDisplayData((prev) => ({
        ...prev,
        isApplicationHold: holdStatus === "hold",
        currentStatusCode: holdStatus,
      }));
      setHoldRemark("");
      toast.success(
        response.message || "Processing status updated successfully",
      );
    } catch {
      toast.error("Failed to save hold status. Please try again.");
    } finally {
      setSavingHoldStatus(false);
    }
  };

  // Check for name discrepancies
  const hasFirstNameDiscrepancy =
    applicant.firstName !== applicant.passportFirstName;
  const hasLastNameDiscrepancy =
    applicant.lastName !== applicant.passportLastName;

  const APPLICANT_TYPES: ApplicantType[] = ["Adult", "Child", "Infant"];

  const [reviewRemark, setReviewRemark] = useState("");

  const effectiveStatusCode =
    !isBatchApplication &&
    displayData.currentStatusCode?.toLowerCase() === "submitted"
      ? "in_progress"
      : displayData.currentStatusCode;

  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] =
    useState<VisaRequestedDetailModel>(applicant);

  const [conversationMessages, setConversationMessages] = useState<
    ReviewMessage[]
  >([]);

  const [isDiscrepancyOpen, setIsDiscrepancyOpen] = useState(false);

  const { canAdd, canEdit } = useScreenPermission();

  // const updateField = <K extends keyof VisaRequestedDetailModel>(
  //   key: K,
  //   value: VisaRequestedDetailModel[K]
  //   ) => {
  //     console.log("Updating field", key, value);
  //   setEditableData((prev) => ({
  //     ...prev,
  //     [key]: value,
  //   }));
  // };

  const [isNameDiscrepancyApproved, setIsNameDiscrepancyApproved] =
    useState(false);

  const [showDocHistory, setShowDocHistory] = useState<Record<number, boolean>>(
    {},
  );

  const loggedInUserId = Number(localStorage.getItem("userId")) || 1;
  const [showFamilyDetails, setShowFamilyDetails] = useState(false);

  const isFinalStatus =
    displayData.currentStatusCode?.toLowerCase() === "submitted" ||
    displayData.currentStatusCode?.toLowerCase() === "documents_approved" ||
    displayData.currentStatusCode?.toLowerCase() === "system_approved";

  const hasNameDiscrepancyUI =
    displayData.firstName !== displayData.passportFirstName ||
    displayData.lastName !== displayData.passportLastName;

  // console.log("appli", applicant);

  // useEffect(() => {
  //   setEditableData(applicant);
  // }, [applicant]);

  useEffect(() => {
    setPageHeader({
      title: "Applicant Details",
      icon: <FileUser className="h-4 w-4" />,
      onBack: () => navigate("/visa-muwafiqa/visa-document-required"),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader]);

  useEffect(() => {
    const approvedStatuses = [
      "submitted",
      "documents_approved",
      "system_approved",
    ];

    setIsNameDiscrepancyApproved(
      approvedStatuses.includes(
        applicant.currentStatusCode?.toLowerCase() ?? "",
      ),
    );
  }, [applicant.currentStatusCode]);

  useEffect(() => {
    if (!applicant.validationErrors) return;

    try {
      const parsedErrors = JSON.parse(applicant.validationErrors);

      const combinedRemarks = parsedErrors
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((err: any) => err.isValid === false && err.remark)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((err: any) => err.remark)
        .join("\n");

      setDisplayData((prev) => ({
        ...prev,
        nameChangeRemark: combinedRemarks,
      }));
    } catch (error) {
      console.error("Failed to parse validationErrors", error);
    }
  }, [applicant.validationErrors]);

  useEffect(() => {
    const fetchConversation = async () => {
      try {
        const data = await getVisaReviewConversation(
          applicant.visaApplicationId,
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mappedMessages: ReviewMessage[] = data.map((item: any) => {
          const date = new Date(item.createdOn);

          return {
            id: item.reviewConversationId.toString(),
            sender: item.isAdmin ? "backoffice" : "tour_operator",
            senderName: item.senderName,
            message: item.message,
            timestamp: date.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            dateTime: date.toLocaleDateString(),
          };
        });

        setConversationMessages(mappedMessages);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load conversation");
      }
    };

    if (!isInBatch) {
      fetchConversation();
    }
  }, [applicant.visaApplicationId, isInBatch]);

  const nameChangeRemark = useMemo(() => {
    if (!applicant.validationErrors) return "";

    try {
      const parsed = JSON.parse(applicant.validationErrors);

      return (
        parsed
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((e: any) => !e.isValid && e.remark)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((e: any) => `• ${e.field}: ${e.remark}`)
          .join("\n")
      );
    } catch {
      return "";
    }
  }, [applicant.validationErrors]);

  const buildUpdatePayload = (
    data: VisaRequestedDetailModel,
  ): UpdateVisaApplicationPayload => ({
    visaApplicationId: data.visaApplicationId,

    title: data.title,
    firstName: data.firstName,
    middleName: data.middleName || "",
    lastName: data.lastName,

    dateOfBirth: `${data.dateOfBirth}T00:00:00`,
    age: Number(data.age),
    gender: data.gender,
    birthPlace: data.birthPlace,
    nationality: data.nationality,
    profession: data.profession,

    applicantType: data.applicantType,
    category: data.category,

    guardianITS: data.guardianIts,
    hofITS: data.hofIts,

    visaRequired: data.visaRequired,
    visaNotRequiredRemark: data.visaRequired ? null : "",
    isMiqaatApplicant: data.miqqatApplicant,

    passportNo: data.passportNo,
    passportType: data.passportType,
    passportFirstName: data.passportFirstName,
    passportMiddleName: data.passportMiddleName,
    passportLastName: data.passportLastName,
    passportArabicName: data.passportArabicName,

    passportDOB: data.passportDob
      ? `${data.passportDob.split("T")[0]}T00:00:00`
      : null,

    passportGender: data.passportGender,
    passportNationality: data.passportNationality,

    passportIssueDate: data.passportIssueDate
      ? `${data.passportIssueDate}`
      : null,

    passportExpiryDate: data.passportExpiryDate
      ? `${data.passportExpiryDate}`
      : null,

    passportIssuePlace: data.passportIssuePlace,

    fatherName: data.fatherName,
    fatherNameArabic: data.fatherNameArabic,
    fatherITSNo: data.fatherIts,

    grandfatherName: data.grandfatherName,
    grandfatherNameArabic: data.grandfatherNameArabic,
    grandfatherITSNo: data.grandfatherIts,

    motherName: data.motherName,
    motherNameArabic: data.motherNameArabic,
    motherITSNo: data.motherIts,

    mothersFatherName: data.mothersFatherName,
    mothersFatherNameArabic: data.mothersFatherNameArabic,
    mothersFatherITSNo: data.mothersFatherIts,
  });

  // useEffect(() => {
  //   setDisplayData(applicant);
  // }, [applicant]);

  // useEffect(() => {
  //   setDisplayData(applicant);
  //   setEditableData(applicant);
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [applicant.visaApplicationId]);

  const getStatusBadge = (status?: string) => {
    if (!status) return null;

    switch (status.toLowerCase()) {
      case "review_needed":
        return (
          <Badge
            variant="outline"
            className="bg-orange-50 text-orange-700 border-orange-200"
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            Review Needed
          </Badge>
        );

      case "in_progress":
        return (
          <Badge
            variant="outline"
            className="bg-yellow-50 text-yellow-700 border-yellow-200"
          >
            <Clock className="w-3 h-3 mr-1" />
            In Progress
          </Badge>
        );

      case "submitted":
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Submitted
          </Badge>
        );

      case "pending_approval":
        return (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200"
          >
            <Clock className="w-3 h-3 mr-1" />
            Pending Approval
          </Badge>
        );

      case "reverted":
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-200"
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            Reverted
          </Badge>
        );

      case "documents_approved":
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Documents Approved
          </Badge>
        );

      case "exported":
        return (
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-200"
          >
            <Download className="w-3 h-3 mr-1" />
            Exported
          </Badge>
        );

      case "uploaded":
        return (
          <Badge
            variant="outline"
            className="bg-cyan-50 text-cyan-700 border-cyan-200"
          >
            <FileStack className="w-3 h-3 mr-1" />
            Uploaded
          </Badge>
        );

      case "batch_added":
        return (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-200"
          >
            <Package className="w-3 h-3 mr-1" />
            Batch Added
          </Badge>
        );
      case "on_hold":
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-500 border-red-200 text-xs"
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            On Hold
          </Badge>
        );

      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleSendReviewRemark = async () => {
    if (!reviewRemark.trim()) {
      toast.error("Please enter a remark before sending");
      return;
    }

    try {
      const payload = {
        visaApplicationId: applicant.visaApplicationId,
        message: reviewRemark,
        isInternal: false, // Admin sending message
      };

      const response = await sendVisaReviewMessage(payload);

      const now = new Date();

      const newMessage: ReviewMessage = {
        id: response.reviewConversationId.toString(),
        sender: "backoffice",
        senderName: user?.name || "Admin",
        message: reviewRemark,
        timestamp: now.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        dateTime: now.toLocaleDateString(),
      };

      setConversationMessages((prev) => [...prev, newMessage]);

      toast.success("Review remark sent successfully");
      setReviewRemark("");
    } catch (error) {
      console.error(error);
      toast.error("Failed to send review remark");
    }
  };

  const handleApproveNameDiscrepancy = async () => {
    try {
      // await changeVisaApplicationStatus(applicant.visaApplicationId, {
      //   visaApplicationId: applicant.visaApplicationId,
      //   statusCode: "SUBMITTED",
      //   messageTitle: "Name discrepancy approved",
      //   message: "Name discrepancy approved",
      //   internalNote: "",
      //   changedByUserId: loggedInUserId,
      // });

      await changeVisaApplicationNameDiscrepancy(applicant.visaApplicationId);

      toast.success("Name discrepancy approved & application submitted");

      setIsNameDiscrepancyApproved(true);

      setDisplayData((prev) => ({
        ...prev,
        currentStatusCode: "SUBMITTED",
      }));

      setTimeout(() => {
        onBack?.();
      }, 800);
    } catch (err) {
      console.error(err);
      toast.error("Failed to approve name discrepancy");
    }
  };

  const getApplicantTypeIcon = (type?: string) => {
    switch (type) {
      case "Infant":
        return <Baby className="w-4 h-4 text-[#5B9BD5]" />;
      case "Child":
        return <UserCircle className="w-4 h-4 text-[#5B9BD5]" />;
      case "Adult":
      default:
        return <User className="w-4 h-4 text-[#5B9BD5]" />;
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return null;

    // Handles "2026-02-06T00:00:00"
    return value.split("T")[0];
  };

  const InfoRow = ({
    label,
    value,
    icon,
    required,
    fallback = "-",
  }: {
    label: string;
    value?: string | number | null;
    icon?: React.ReactNode;
    required?: boolean;
    fallback?: string;
  }) => {
    const hasValue = value !== null && value !== undefined && value !== "";

    return (
      <div className="flex items-start gap-3 py-1.5">
        {icon && <div className="mt-0.5">{icon}</div>}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 mb-0.5">
            {label} {required && <span className="text-red-500">*</span>}
          </p>
          <p className="text-sm text-gray-900 font-medium break-words">
            {hasValue ? (
              value
            ) : (
              <span className="text-gray-400">{fallback}</span>
            )}
          </p>
        </div>
      </div>
    );
  };

  const handleViewDocument = (file?: string) => {
    if (!file) {
      toast.error("No document available");
      return;
    }

    toast.success(`Viewing ${file}`);
  };

  const EditableText = React.memo(
    ({
      label,
      value,
      onChange,
      required,
      type = "text",
    }: {
      label: string;
      value: string | number;
      onChange: (v: string) => void;
      required?: boolean;
      type?: string;
    }) => (
      <div>
        {label && (
          <p className="text-xs text-gray-500 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
          </p>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ padding: "10px" }}
          className="
      w-full
      h-9
      px-3
      text-sm
      text-gray-900
      bg-gray-50
      rounded-xl
      border border-transparent
      focus:outline-none
      focus:bg-white
      focus:border-[#5B9BD5]
      focus:ring-2
      focus:ring-[#5B9BD5]/20
      transition
    "
        />
      </div>
    ),
  );

  const ViewField = ({
    label,
    value,
    required,
  }: {
    label: string;
    value?: string;
    required?: boolean;
  }) => (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">
        {label} {required && <span className="text-red-500">*</span>}
      </p>
      <p className="text-sm text-gray-900 font-medium">
        {value || <span className="text-gray-400">-</span>}
      </p>
    </div>
  );

  const SectionTitle = ({ title }: { title: string }) => (
    <p className="text-xs font-medium text-gray-700">{title}</p>
  );

  const SectionHeader = ({
    icon,
    title,
  }: {
    icon: React.ReactNode;
    title: string;
  }) => (
    <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-[#5B9BD5]">
      <span className="text-[#5B9BD5]">{icon}</span>
      <h3 className="font-semibold text-gray-900">{title}</h3>
    </div>
  );

  const DiscrepancyField = ({
    label,
    value,
    highlight,
  }: {
    label: string;
    value: string;
    highlight?: boolean;
  }) => (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p
        className={`text-sm font-medium px-2 py-1 rounded ${
          highlight
            ? "bg-orange-100 text-orange-900 border border-orange-300"
            : "text-gray-900"
        }`}
      >
        {value}
      </p>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 pb-6 md:pb-8 px-2 md:px-0">
      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Add Rejection Remark
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectRemark("");
                }}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectRemark}
                  onChange={(e) => setRejectRemark(e.target.value)}
                  placeholder="Please provide a detailed reason for rejecting this application..."
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectRemark("");
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (rejectRemark.trim()) {
                      toast.error(
                        `❌ Application rejected! Remark: "${rejectRemark}"`,
                      );
                      setShowRejectDialog(false);
                      setRejectRemark("");
                    } else {
                      toast.error("Please provide a remark before rejecting.");
                    }
                  }}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Submit Rejection
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-3 border-b pb-2">
        <div className="flex items-center gap-2 md:gap-3">
          {getStatusBadge(effectiveStatusCode)}
          <div className="flex gap-2">
            {canEdit && !isBatchApplication && (
              <>
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="h-9 px-4 border-2"
                    style={{ borderColor: "#5B9BD5", color: "#5B9BD5" }}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Details
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditableData(applicant);
                        setIsEditing(false);
                        toast.info("Changes cancelled");
                      }}
                      className="h-9 px-4"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const payload = buildUpdatePayload(editableData);
                          await updateVisaApplication(payload);
                          toast.success("Details updated successfully");
                          setEditableData((prev) => ({ ...prev }));
                          setTimeout(() => {
                            onBack?.();
                          }, 500);
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to update details");
                        }
                      }}
                      className="h-9 px-4 text-white"
                      style={{ backgroundColor: "#5B9BD5" }}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save All Changes
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Name Discrepancy Warning */}
      {hasNameDiscrepancyUI && (
        <Card className="border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-red-50 shadow-lg">
          <CardContent className="p-6">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsDiscrepancyOpen((prev) => !prev)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>

                <div>
                  <h3 className="font-bold text-orange-900 flex items-center gap-2">
                    Name Discrepancy Detected
                    <Badge
                      variant="outline"
                      className="bg-orange-100 text-orange-700 border-orange-300"
                    >
                      Requires Admin Review
                    </Badge>
                  </h3>
                </div>
              </div>

              {isDiscrepancyOpen ? (
                <ChevronUp className="w-5 h-5 text-orange-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-orange-700" />
              )}
            </div>

            {/* Collapsible Content */}
            {isDiscrepancyOpen && (
              <div className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 p-3 md:p-4 rounded-lg border-2 border-orange-200">
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2 md:mb-3">
                      System Name (FHIMS Records)
                    </p>
                    <div className="space-y-1.5 md:space-y-2">
                      <p className="text-xs md:text-sm">
                        <span className="text-gray-500">First Name:</span>{" "}
                        <span className="font-semibold text-gray-900">
                          {displayData.firstName}
                        </span>
                      </p>

                      <p className="text-xs md:text-sm">
                        <span className="text-gray-500">Last Name:</span>{" "}
                        <span className="font-semibold text-gray-900">
                          {displayData.lastName}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2 md:mb-3">
                      Passport Name
                    </p>
                    <div className="space-y-1.5 md:space-y-2">
                      <p className="text-xs md:text-sm">
                        <span className="text-gray-500">First Name:</span>{" "}
                        <span className="font-semibold text-orange-700">
                          {displayData.passportFirstName}
                        </span>
                      </p>

                      <p className="text-xs md:text-sm">
                        <span className="text-gray-500">Last Name:</span>{" "}
                        <span className="font-semibold text-orange-700">
                          {displayData.passportLastName}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border-2 border-orange-200 mb-4">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    Remark:
                  </p>
                  <p className="text-sm text-gray-800 whitespace-pre-line">
                    {nameChangeRemark}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    disabled={isNameDiscrepancyApproved}
                    onClick={handleApproveNameDiscrepancy}
                    className={
                      isNameDiscrepancyApproved
                        ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                        : "bg-green-700 hover:bg-green-700 text-white"
                    }
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {isNameDiscrepancyApproved ? "Approved" : "Approve"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {/* Quick Summary Cards */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4"
        style={{ marginTop: "0px" }}
      >
        <Card className="bg-gradient-to-br from-white to-sky-50 border-2 border-sky-200 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5B9BD5] to-purple-600 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Application No</p>
                <p className="text-sm font-semibold text-gray-900">
                  {applicant?.applicationNumber}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-blue-50 border-2 border-blue-200 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-600">ITS Number</p>
                <p className="text-sm font-semibold text-gray-900">
                  {applicant.itsNumber ?? applicant?.itsNo}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-green-50 border-2 border-green-200 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Passport No</p>
                <p className="text-sm font-semibold text-gray-900">
                  {applicant.passportNo}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-purple-50 border-2 border-purple-200 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Visa Fee</p>
                <p className="text-sm font-semibold text-gray-900">
                  {applicant.visaRequired ? `$${applicant.visaCost}` : "N/A"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 md:gap-2 border-b overflow-x-auto"
        style={{ marginTop: "10px" }}
      >
        <button
          onClick={() => setActiveTab("details")}
          className={`px-3 md:px-6 py-2 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "details"
              ? "border-[#5B9BD5] text-[#5B9BD5]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <User className="w-3 h-3 md:w-4 md:h-4 inline-block mr-1 md:mr-2" />
          Details
        </button>
        <button
          onClick={() => setActiveTab("documents")}
          className={`px-3 md:px-6 py-2 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "documents"
              ? "border-[#5B9BD5] text-[#5B9BD5]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <FileStack className="w-3 h-3 md:w-4 md:h-4 inline-block mr-1 md:mr-2" />
          Docs ({applicant.uploadedDocuments}/{applicant.requiredDocuments})
        </button>
        {!isBatchApplication && (
          <button
            onClick={() => setActiveTab("hold")}
            className={`px-3 md:px-6 py-2 md:py-3 text-xs md:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "hold"
                ? "border-[#5B9BD5] text-[#5B9BD5]"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <PauseCircle className="w-3 h-3 md:w-4 md:h-4 inline-block mr-1 md:mr-2" />
            Hold
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === "details" && (
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-2 md:gap-2"
          style={{ marginTop: "10px" }}
        >
          {/* Left Column */}
          <div className="space-y-6">
            {/* Personal Information */}
            <Card
              className="bg-white border-2 border-sky-200 shadow-md"
              style={{ padding: "0px" }}
            >
              <CardContent style={{ padding: "15px" }}>
                <div
                  className="flex items-center gap-2 mb-3 pb-2 border-b-2"
                  style={{ borderColor: "#5B9BD5" }}
                >
                  <User className="w-5 h-5" style={{ color: "#5B9BD5" }} />
                  <h3 className="font-semibold text-gray-900">
                    Personal Information
                  </h3>
                </div>

                <div className="space-y-3">
                  {/* ITS Number & User Type (read-only always) */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">
                        ITS Number <span className="text-red-500">*</span>{" "}
                      </p>
                      <p className="text-sm text-gray-900 font-medium">
                        {" "}
                        {applicant.itsNumber}{" "}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">User Type</p>
                      <div className="mt-1">
                        <div className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300 inline-block">
                          ITS User
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Title, First, Middle, Last Name */}
                  <div className="grid grid-cols-4 gap-3">
                    {!isEditing ? (
                      <>
                        <ViewField
                          label="Title"
                          value={applicant.title}
                          required
                        />
                        <ViewField
                          label="First Name"
                          value={applicant.firstName}
                          required
                        />
                        <ViewField
                          label="Middle Name"
                          value={applicant.middleName}
                        />
                        <ViewField
                          label="Last Name"
                          value={applicant.lastName}
                          required
                        />
                      </>
                    ) : (
                      <>
                        <EditableText
                          label="Title"
                          required
                          value={editableData.title}
                          onChange={(v) =>
                            setEditableData({ ...editableData, title: v })
                          }
                        />
                        <EditableText
                          label="First Name"
                          required
                          value={editableData.firstName}
                          onChange={(v) =>
                            setEditableData({ ...editableData, firstName: v })
                          }
                        />
                        <EditableText
                          label="Middle Name"
                          value={editableData.middleName}
                          onChange={(v) =>
                            setEditableData({ ...editableData, middleName: v })
                          }
                        />
                        <EditableText
                          label="Last Name"
                          required
                          value={editableData.lastName}
                          onChange={(v) =>
                            setEditableData({ ...editableData, lastName: v })
                          }
                        />
                      </>
                    )}
                  </div>

                  {/* DOB, Age, Gender, Birth Place */}
                  <div className="grid grid-cols-4 gap-3">
                    {!isEditing ? (
                      <>
                        <ViewField
                          label="Date of Birth"
                          value={applicant.dateOfBirth}
                          required
                        />
                        <ViewField label="Age" value={`${applicant.age}`} />
                        <ViewField
                          label="Gender"
                          value={applicant.gender}
                          required
                        />
                        <ViewField
                          label="Birth Place"
                          value={applicant.birthPlace}
                        />
                      </>
                    ) : (
                      <>
                        <EditableText
                          label="Date of Birth"
                          type="date"
                          required
                          value={editableData.dateOfBirth}
                          onChange={(v) =>
                            setEditableData({ ...editableData, dateOfBirth: v })
                          }
                        />
                        <EditableText
                          label="Age"
                          type="number"
                          value={editableData.age}
                          onChange={(v) =>
                            setEditableData({
                              ...editableData,
                              age: Number(v),
                            })
                          }
                        />

                        <EditableText
                          label="Gender"
                          required
                          value={editableData.gender}
                          onChange={(v) =>
                            setEditableData({ ...editableData, gender: v })
                          }
                        />
                        <EditableText
                          label="Birth Place"
                          value={editableData.birthPlace}
                          onChange={(v) =>
                            setEditableData({ ...editableData, birthPlace: v })
                          }
                        />
                      </>
                    )}
                  </div>

                  {/* Nationality, Applicant Type, Category, Guardian ITS */}
                  <div className="grid grid-cols-4 gap-3">
                    {!isEditing ? (
                      <>
                        <ViewField
                          label="Nationality"
                          value={applicant.nationality}
                          required
                        />
                        <ViewField
                          label="Applicant Type"
                          value={applicant.applicantType}
                          required
                        />
                        <ViewField
                          label="Category"
                          value={applicant.category}
                          required
                        />
                        <ViewField
                          label="Guardian ITS"
                          value={applicant.guardianIts}
                        />
                      </>
                    ) : (
                      <>
                        <EditableText
                          label="Nationality"
                          required
                          value={editableData.nationality}
                          onChange={(v) =>
                            setEditableData({ ...editableData, nationality: v })
                          }
                        />
                        {isEditing ? (
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">
                              Applicant Type{" "}
                              <span className="text-red-500">*</span>
                            </p>

                            <Select
                              value={editableData.applicantType}
                              onValueChange={(
                                v: "Adult" | "Child" | "Infant" | "Senior",
                              ) =>
                                setEditableData({
                                  ...editableData,
                                  applicantType: v,
                                })
                              }
                            >
                              <SelectTrigger
                                className="
                                        w-full
                                        bg-transparent
                                        border-0
                                        p-0
                                        h-auto
                                        focus:ring-0
                                        focus:outline-none
                                      "
                              >
                                <div className="flex items-center gap-2 mt-1 cursor-pointer">
                                  {/* ✅ ICON BACK */}
                                  {getApplicantTypeIcon(
                                    editableData.applicantType,
                                  )}

                                  <Badge
                                    variant="outline"
                                    className="bg-sky-50 text-sky-700 border-sky-200"
                                  >
                                    {editableData.applicantType}
                                  </Badge>
                                </div>
                              </SelectTrigger>

                              <SelectContent>
                                <SelectItem value="Adult">
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-[#5B9BD5]" />
                                    Adult
                                  </div>
                                </SelectItem>

                                <SelectItem value="Child">
                                  <div className="flex items-center gap-2">
                                    <UserCircle className="w-4 h-4 text-[#5B9BD5]" />
                                    Child
                                  </div>
                                </SelectItem>

                                <SelectItem value="Infant">
                                  <div className="flex items-center gap-2">
                                    <Baby className="w-4 h-4 text-[#5B9BD5]" />
                                    Infant
                                  </div>
                                </SelectItem>

                                <SelectItem value="Senior">
                                  <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-[#5B9BD5]" />
                                    Senior
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <ViewField
                            label="Applicant Type"
                            value={applicant.applicantType}
                            required
                          />
                        )}

                        <EditableText
                          label="Category"
                          required
                          value={editableData.category}
                          onChange={(v) =>
                            setEditableData({ ...editableData, category: v })
                          }
                        />
                        <EditableText
                          label="Guardian ITS"
                          value={editableData.guardianIts}
                          onChange={(v) =>
                            setEditableData({ ...editableData, guardianIts: v })
                          }
                        />
                      </>
                    )}
                  </div>

                  {/* HOF ITS */}
                  <div className="grid grid-cols-2 gap-3">
                    {!isEditing ? (
                      <ViewField label="HOF ITS" value={applicant.hofIts} />
                    ) : (
                      <EditableText
                        label="HOF ITS"
                        value={editableData.hofIts}
                        onChange={(v) =>
                          setEditableData({ ...editableData, hofIts: v })
                        }
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">
                        Visa Required
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <div
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            applicant.visaRequired
                              ? "bg-green-100 text-green-700 border border-green-300"
                              : "bg-gray-100 text-gray-700 border border-gray-300"
                          }`}
                        >
                          {applicant.visaRequired ? "Yes" : "No"}
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">
                        Miqqat Applicant
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <div
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            applicant.miqqatApplicant
                              ? "bg-green-100 text-green-700 border border-green-300"
                              : "bg-gray-100 text-gray-700 border border-gray-300"
                          }`}
                        >
                          {applicant.miqqatApplicant ? "Yes" : "No"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Application Status */}
            <Card className="bg-white border-2 border-sky-200 shadow-md">
              <CardContent style={{ padding: "15px" }}>
                <div
                  className="flex items-center gap-2 mb-4 pb-3 border-b-2"
                  style={{ borderColor: "#5B9BD5" }}
                >
                  <CheckCircle2
                    className="w-5 h-5"
                    style={{ color: "#5B9BD5" }}
                  />
                  <h3 className="font-semibold text-gray-900">
                    Application Status
                  </h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">Current Status</p>
                    {getStatusBadge(effectiveStatusCode)}
                  </div>
                  <InfoRow
                    label="Review Type"
                    value={
                      applicant.reviewStatus === "automatic"
                        ? "Automatic Review"
                        : "Manual Review"
                    }
                  />
                  {applicant.batchNo && (
                    <>
                      <Separator className="my-3" />
                      <InfoRow label="Batch Number" value={applicant.batchNo} />
                      {/* <InfoRow label="Batch Name" value={applicant.batchName} /> */}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tour Information */}
            <Card className="bg-white border-2 border-sky-200 shadow-md">
              <CardContent style={{ padding: "15px" }}>
                <div
                  className="flex items-center gap-2 mb-3 pb-2 border-b-2"
                  style={{ borderColor: "#5B9BD5" }}
                >
                  <Plane className="w-5 h-5" style={{ color: "#5B9BD5" }} />
                  <h3 className="font-semibold text-gray-900">
                    Tour Information
                  </h3>
                </div>
                <div className="space-y-2">
                  {/* <div>
                    <p className="text-xs text-gray-500 mb-0.5">
                      Tour Reference Number
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {applicant.tourRefNo || "Direct Application"}
                    </p>
                  </div> */}
                  {/* <div>
                    <p className="text-xs text-gray-500 mb-0.5">Tour Details</p>
                    <p className="text-sm text-gray-900 font-medium">
                      {applicant.tourInfo || (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </p>
                  </div> */}
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">
                      Partner Office
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {applicant.partnerOffice}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Applied Date</p>
                    <p className="text-sm text-gray-900 font-medium">
                      {applicant.appliedDate}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Remarks */}
            {applicant.tourOperatorRemark && (
              <Card className="border-2 border-amber-200 bg-amber-50 shadow-md">
                <CardContent style={{ padding: "15px" }}>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-amber-300">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-amber-900">
                      Tour Operator Remarks
                    </h3>
                  </div>
                  <p className="text-sm text-amber-800">
                    {applicant.tourOperatorRemark}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Review Conversation & Remarks Section - Full Width */}
            {canAdd && !isBatchApplication && (
              <Card className="border-2 border-sky-200 shadow-md mt-6">
                <CardContent className="p-6">
                  <div
                    className="flex items-center gap-2 mb-4 pb-3 border-b-2"
                    style={{ borderColor: "#5B9BD5" }}
                  >
                    <MessageSquare
                      className="w-5 h-5"
                      style={{ color: "#5B9BD5" }}
                    />
                    <h3 className="font-semibold text-gray-900">
                      Review Conversation
                    </h3>
                  </div>

                  {/* Conversation Thread - Show at Top */}
                  {conversationMessages && conversationMessages.length > 0 ? (
                    <div className="mb-6">
                      <div className="bg-gradient-to-br from-blue-50 via-sky-50 to-blue-50 rounded-lg p-5 border-2 border-[#5B9BD5]/30 shadow-sm">
                        <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 pb-2 border-b border-[#5B9BD5]/20">
                          <Clock
                            className="w-4 h-4"
                            style={{ color: "#5B9BD5" }}
                          />
                          Entire Conversation Log ({conversationMessages.length}{" "}
                          messages)
                        </h4>
                        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                          {conversationMessages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`rounded-xl p-4 shadow-sm border-2 transition-all hover:shadow-md ${
                                msg.sender === "backoffice"
                                  ? "bg-gradient-to-br from-blue-50 to-white border-blue-300"
                                  : "bg-gradient-to-br from-green-50 to-white border-green-300"
                              }`}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <span className="text-sm font-bold text-gray-900">
                                    {msg.senderName}
                                  </span>
                                </div>
                                <div className="text-right ml-4">
                                  <p className="text-xs font-semibold text-gray-600">
                                    {msg.timestamp}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {msg.dateTime}
                                  </p>
                                </div>
                              </div>
                              <div
                                className={`pl-3 border-l-4 ${
                                  msg.sender === "backoffice"
                                    ? "border-[#5B9BD5]"
                                    : "border-green-500"
                                }`}
                              >
                                <p className="text-sm text-gray-800 leading-relaxed">
                                  {msg.message}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
                      <div className="text-center">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-sm font-semibold text-gray-600 mb-1">
                          No messages yet
                        </p>
                        <p className="text-xs text-gray-500">
                          Add a review remark below to start the conversation
                          with the tour operator.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Add Review Remark */}
                  <div
                    className="border-t-2 pt-6"
                    style={{ borderColor: "#5B9BD5" }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <MessageSquare
                        className="w-5 h-5"
                        style={{ color: "#5B9BD5" }}
                      />
                      <h4 className="font-semibold text-gray-900">
                        Add Review Remark
                      </h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Review Remark / Message to Tour Operator
                        </label>
                        <textarea
                          value={reviewRemark}
                          onChange={(e) => setReviewRemark(e.target.value)}
                          placeholder="Type your message or review remark here. This will be sent to the tour operator and appear in the conversation log above..."
                          className="w-full h-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B9BD5] focus:border-transparent resize-none text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          💡 This remark will be sent to the tour operator and
                          added to the conversation thread above with your name
                          and timestamp.
                        </p>
                      </div>
                      <div className="flex items-center justify-end gap-3 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setReviewRemark("");
                            toast.info("Remark cleared");
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          style={{ backgroundColor: "#5B9BD5" }}
                          className="text-white"
                          onClick={handleSendReviewRemark}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Send Review Remark
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Passport Information */}
            <Card
              className="bg-white border-2 border-sky-200 shadow-md"
              style={{ marginTop: "0px" }}
            >
              <CardContent style={{ padding: "15px" }}>
                <SectionHeader
                  icon={<CreditCard />}
                  title="Passport Information"
                />

                <div className="space-y-3">
                  {!isEditing ? (
                    <>
                      <ViewField
                        label="Passport Number"
                        value={applicant.passportNo}
                      />
                      <ViewField
                        label="Passport Type"
                        value={applicant.passportType}
                      />
                    </>
                  ) : (
                    <>
                      <EditableText
                        label="Passport Number"
                        value={editableData.passportNo}
                        onChange={(v) =>
                          setEditableData({ ...editableData, passportNo: v })
                        }
                      />
                      <EditableText
                        label="Passport Type"
                        value={editableData.passportType}
                        onChange={(v) =>
                          setEditableData({ ...editableData, passportType: v })
                        }
                      />
                    </>
                  )}

                  <Separator />

                  {/* Name on Passport */}
                  <p className="text-xs font-medium text-gray-700">
                    Name on Passport
                  </p>

                  {/* First Name */}
                  {!isEditing ? (
                    <DiscrepancyField
                      label="First Name"
                      value={applicant.passportFirstName}
                      highlight={hasFirstNameDiscrepancy}
                    />
                  ) : (
                    <EditableText
                      label="First Name"
                      value={editableData.passportFirstName}
                      onChange={(v) =>
                        setEditableData({
                          ...editableData,
                          passportFirstName: v,
                        })
                      }
                    />
                  )}

                  {/* Middle Name */}
                  {!isEditing ? (
                    <ViewField
                      label="Middle Name"
                      value={applicant.passportMiddleName}
                    />
                  ) : (
                    <EditableText
                      label="Middle Name"
                      value={editableData.passportMiddleName}
                      onChange={(v) =>
                        setEditableData({
                          ...editableData,
                          passportMiddleName: v,
                        })
                      }
                    />
                  )}

                  {/* Last Name */}
                  {!isEditing ? (
                    <DiscrepancyField
                      label="Last Name"
                      value={applicant.passportLastName}
                      highlight={hasLastNameDiscrepancy}
                    />
                  ) : (
                    <EditableText
                      label="Last Name"
                      value={editableData.passportLastName}
                      onChange={(v) =>
                        setEditableData({
                          ...editableData,
                          passportLastName: v,
                        })
                      }
                    />
                  )}

                  {/* Arabic Name */}
                  {!isEditing ? (
                    <ViewField
                      label="Arabic Name"
                      value={applicant.passportArabicName}
                    />
                  ) : (
                    <EditableText
                      label="Arabic Name"
                      value={editableData.passportArabicName}
                      onChange={(v) =>
                        setEditableData({
                          ...editableData,
                          passportArabicName: v,
                        })
                      }
                    />
                  )}

                  <Separator />

                  {/* DOB / Gender */}
                  <div className="grid grid-cols-2 gap-3">
                    {!isEditing ? (
                      <>
                        <ViewField
                          label="Date of Birth"
                          value={formatDate(applicant.passportDob) ?? "-"}
                        />
                        <ViewField
                          label="Gender"
                          value={applicant.passportGender ?? "-"}
                        />
                      </>
                    ) : (
                      <>
                        <EditableText
                          label="Date of Birth"
                          type="date"
                          value={formatDate(editableData.passportDob) ?? ""}
                          onChange={(v) =>
                            setEditableData({ ...editableData, passportDob: v })
                          }
                        />
                        <EditableText
                          label="Gender"
                          value={editableData.passportGender ?? ""}
                          onChange={(v) =>
                            setEditableData({
                              ...editableData,
                              passportGender: v,
                            })
                          }
                        />
                      </>
                    )}
                  </div>

                  {!isEditing ? (
                    <ViewField
                      label="Nationality"
                      value={applicant.passportNationality}
                    />
                  ) : (
                    <EditableText
                      label="Nationality"
                      value={editableData.passportNationality}
                      onChange={(v) =>
                        setEditableData({
                          ...editableData,
                          passportNationality: v,
                        })
                      }
                    />
                  )}

                  <Separator />

                  {/* Issue / Expiry */}
                  <div className="grid grid-cols-2 gap-3">
                    {!isEditing ? (
                      <>
                        <ViewField
                          label="Issue Date"
                          value={formatDate(applicant.passportIssueDate) ?? "-"}
                        />
                        <ViewField
                          label="Expiry Date"
                          value={
                            formatDate(applicant.passportExpiryDate) ?? "-"
                          }
                        />
                      </>
                    ) : (
                      <>
                        <EditableText
                          label="Issue Date"
                          type="date"
                          value={
                            formatDate(editableData.passportIssueDate) ?? ""
                          }
                          onChange={(v) =>
                            setEditableData({
                              ...editableData,
                              passportIssueDate: v,
                            })
                          }
                        />
                        <EditableText
                          label="Expiry Date"
                          type="date"
                          value={
                            formatDate(editableData.passportExpiryDate) ?? ""
                          }
                          onChange={(v) =>
                            setEditableData({
                              ...editableData,
                              passportExpiryDate: v,
                            })
                          }
                        />
                      </>
                    )}
                  </div>

                  {!isEditing ? (
                    <ViewField
                      label="Issue Place"
                      value={applicant.passportIssuePlace}
                    />
                  ) : (
                    <EditableText
                      label="Issue Place"
                      value={editableData.passportIssuePlace}
                      onChange={(v) =>
                        setEditableData({
                          ...editableData,
                          passportIssuePlace: v,
                        })
                      }
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Family Information */}
            <Card className="bg-white border-2 border-sky-200 shadow-md">
              <CardContent style={{ padding: "15px" }}>
                <div
                  className="flex items-center gap-2 mb-3 pb-2 border-b-2"
                  style={{ borderColor: "#5B9BD5" }}
                >
                  <Users className="w-5 h-5" style={{ color: "#5B9BD5" }} />
                  <h3 className="font-semibold text-gray-900">
                    Family Information
                  </h3>
                </div>

                <div className="space-y-4">
                  {/* Guardian / HOF */}
                  <div className="grid grid-cols-3 gap-3">
                    {!isEditing ? (
                      <>
                        <ViewField
                          label="Guardian ITS Number"
                          value={applicant.guardianIts}
                        />
                        <ViewField
                          label="Head of Family ITS"
                          value={applicant.hofIts}
                        />
                        {/* Toggle Button */}
                        <div
                          className="flex items-end"
                          style={{ justifyContent: "end" }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setShowFamilyDetails((prev) => !prev)
                            }
                            className="p-2 rounded-lg hover:bg-[#5B9BD5]/10 transition"
                            title={
                              showFamilyDetails
                                ? "Hide Details"
                                : "View Details"
                            }
                          >
                            <Eye
                              className={`w-5 h-5 transition ${
                                showFamilyDetails
                                  ? "text-[#5B9BD5]"
                                  : "text-gray-500"
                              }`}
                            />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <EditableText
                          label="Guardian ITS Number"
                          value={editableData.guardianIts}
                          onChange={(v) =>
                            setEditableData({ ...editableData, guardianIts: v })
                          }
                        />
                        <EditableText
                          label="Head of Family ITS"
                          value={editableData.hofIts}
                          onChange={(v) =>
                            setEditableData({ ...editableData, hofIts: v })
                          }
                        />
                      </>
                    )}
                  </div>
                  {showFamilyDetails && (
                    <>
                      <Separator />

                      {/* Father */}
                      <SectionTitle title="Father Details" />

                      {!isEditing ? (
                        <>
                          <ViewField
                            label="Name (English)"
                            value={applicant.fatherName}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <ViewField
                              label="Name (Arabic)"
                              value={applicant.fatherNameArabic}
                            />
                            <ViewField
                              label="ITS Number"
                              value={applicant.fatherIts}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <EditableText
                            label="Name (English)"
                            value={editableData.fatherName}
                            onChange={(v) =>
                              setEditableData({
                                ...editableData,
                                fatherName: v,
                              })
                            }
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <EditableText
                              label="Name (Arabic)"
                              value={editableData.fatherNameArabic}
                              onChange={(v) =>
                                setEditableData({
                                  ...editableData,
                                  fatherNameArabic: v,
                                })
                              }
                            />
                            <EditableText
                              label="ITS Number"
                              value={editableData.fatherIts}
                              onChange={(v) =>
                                setEditableData({
                                  ...editableData,
                                  fatherIts: v,
                                })
                              }
                            />
                          </div>
                        </>
                      )}

                      <Separator />

                      {/* Grandfather */}
                      <SectionTitle title="Grandfather Details" />

                      {!isEditing ? (
                        <>
                          <ViewField
                            label="Name (English)"
                            value={applicant.grandfatherName}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <ViewField
                              label="Name (Arabic)"
                              value={applicant.grandfatherNameArabic}
                            />
                            <ViewField
                              label="ITS Number"
                              value={applicant.grandfatherIts}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <EditableText
                            label="Name (English)"
                            value={editableData.grandfatherName}
                            onChange={(v) =>
                              setEditableData({
                                ...editableData,
                                grandfatherName: v,
                              })
                            }
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <EditableText
                              label="Name (Arabic)"
                              value={editableData.grandfatherNameArabic}
                              onChange={(v) =>
                                setEditableData({
                                  ...editableData,
                                  grandfatherNameArabic: v,
                                })
                              }
                            />
                            <EditableText
                              label="ITS Number"
                              value={editableData.grandfatherIts}
                              onChange={(v) =>
                                setEditableData({
                                  ...editableData,
                                  grandfatherIts: v,
                                })
                              }
                            />
                          </div>
                        </>
                      )}

                      <Separator />

                      {/* Mother */}
                      <SectionTitle title="Mother Details" />

                      {!isEditing ? (
                        <>
                          <ViewField
                            label="Name (English)"
                            value={applicant.motherName}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <ViewField
                              label="Name (Arabic)"
                              value={applicant.motherNameArabic}
                            />
                            <ViewField
                              label="ITS Number"
                              value={applicant.motherIts}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <EditableText
                            label="Name (English)"
                            value={editableData.motherName}
                            onChange={(v) =>
                              setEditableData({
                                ...editableData,
                                motherName: v,
                              })
                            }
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <EditableText
                              label="Name (Arabic)"
                              value={editableData.motherNameArabic}
                              onChange={(v) =>
                                setEditableData({
                                  ...editableData,
                                  motherNameArabic: v,
                                })
                              }
                            />
                            <EditableText
                              label="ITS Number"
                              value={editableData.motherIts}
                              onChange={(v) =>
                                setEditableData({
                                  ...editableData,
                                  motherIts: v,
                                })
                              }
                            />
                          </div>
                        </>
                      )}

                      <Separator />

                      {/* Mother's Father */}
                      <SectionTitle title="Mother's Father Details" />

                      {!isEditing ? (
                        <>
                          <ViewField
                            label="Name (English)"
                            value={applicant.mothersFatherName}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <ViewField
                              label="Name (Arabic)"
                              value={applicant.mothersFatherNameArabic}
                            />
                            <ViewField
                              label="ITS Number"
                              value={applicant.mothersFatherIts}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <EditableText
                            label="Name (English)"
                            value={editableData.mothersFatherName}
                            onChange={(v) =>
                              setEditableData({
                                ...editableData,
                                mothersFatherName: v,
                              })
                            }
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <EditableText
                              label="Name (Arabic)"
                              value={editableData.mothersFatherNameArabic}
                              onChange={(v) =>
                                setEditableData({
                                  ...editableData,
                                  mothersFatherNameArabic: v,
                                })
                              }
                            />
                            <EditableText
                              label="ITS Number"
                              value={editableData.mothersFatherIts}
                              onChange={(v) =>
                                setEditableData({
                                  ...editableData,
                                  mothersFatherIts: v,
                                })
                              }
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Visa Requirements */}
            {/* <Card className="bg-white border-2 border-sky-200 shadow-md">
              <CardContent style={{ padding: "15px" }}>
                <div
                  className="flex items-center gap-2 mb-4 pb-3 border-b-2"
                  style={{ borderColor: "#5B9BD5" }}
                >
                  <Globe className="w-5 h-5" style={{ color: "#5B9BD5" }} />
                  <h3 className="font-semibold text-gray-900">
                    Visa Requirements
                  </h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">Visa Required</p>
                    <Badge
                      variant={applicant.visaRequired ? "default" : "secondary"}
                    >
                      {applicant.visaRequired ? "Yes" : "No"}
                    </Badge>
                  </div>
                  {applicant.visaRequired ? (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-green-900">
                          Visa Cost
                        </p>
                        <p className="text-2xl font-bold text-green-700">
                          ${applicant.visaCost}
                        </p>
                      </div>
                      <p className="text-xs text-green-600">
                        Standard visa processing fee
                      </p>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-blue-900 mb-1">
                            No Visa Required
                          </p>
                          {applicant.tourOperatorRemark && (
                            <p className="text-xs text-blue-700">
                              {applicant.tourOperatorRemark}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card> */}
          </div>
        </div>
      )}

      {activeTab === "documents" && (
        <Card className="bg-white border-2 border-sky-200 shadow-md">
          <CardContent className="p-6 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Document Status</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {applicant.documents?.length ?? 0} documents uploaded
                </p>
              </div>
            </div>

            {/* Documents List */}
            <div className="grid gap-4">
              {applicant.documents?.map((doc) => {
                const latestVersion = doc.versions?.[doc.versions.length - 1];
                const isApproved = doc.status === "APPROVED";

                return (
                  <div
                    key={doc.documentId}
                    className={`border-2 rounded-lg ${
                      isApproved
                        ? "bg-green-50 border-green-200"
                        : "bg-amber-50 border-amber-200"
                    }`}
                  >
                    {/* Document Header */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isApproved ? "bg-green-100" : "bg-amber-100"
                          }`}
                        >
                          {isApproved ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                          )}
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {doc.documentName}
                          </p>
                          <p className="text-xs text-gray-500">
                            Status: {doc.status}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {latestVersion?.filePath && (
                          <button
                            className="hover:bg-gray-100 rounded-lg p-1.5 transition-colors"
                            style={{ color: "#5B9BD5" }}
                          >
                            <Eye
                              className="w-4 h-4 cursor-pointer"
                              onClick={() => {
                                const fullUrl = getFileViewUrl(
                                  latestVersion.filePath,
                                );
                                window.open(fullUrl, "_blank");
                              }}
                            />
                          </button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          style={{ borderColor: "#5B9BD5", color: "#5B9BD5" }}
                          onClick={() =>
                            setShowDocHistory((prev) => ({
                              ...prev,
                              [doc.documentId]: !prev[doc.documentId],
                            }))
                          }
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-2" />
                          History ({doc.versions?.length ?? 0})
                        </Button>
                      </div>
                    </div>

                    {/* Version History */}
                    {showDocHistory[doc.documentId] && (
                      <div className="px-4 pb-4 pt-2 border-t">
                        <p className="text-xs font-semibold text-gray-700 mb-3">
                          Document History
                        </p>

                        <div className="space-y-2">
                          {[...doc.versions].reverse().map((version, idx) => {
                            const isCurrent = idx === 0;

                            return (
                              <div
                                key={version.documentVersionId}
                                className={`bg-white rounded-lg p-3 border ${
                                  isCurrent
                                    ? "border-green-300"
                                    : "border-gray-300"
                                }`}
                              >
                                <div className="flex items-start justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    {isCurrent && (
                                      <Badge className="bg-green-600 text-white text-xs">
                                        Current
                                      </Badge>
                                    )}
                                    <span className="text-xs font-medium">
                                      Version {version.versionNo}
                                    </span>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {version.histories?.[0]?.actionAt?.split(
                                      "T",
                                    )[0] ?? "-"}
                                  </span>
                                </div>

                                <p className="text-xs text-gray-600">
                                  File: {version.fileName}
                                </p>

                                {version.histories?.[0]?.remarks && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Remark: {version.histories[0].remarks}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            {/* <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Download All
              </Button>
            </div> */}
          </CardContent>
        </Card>
      )}

      {!isBatchApplication && activeTab === "hold" && (
        <Card className="bg-white border-2 border-sky-200 shadow-md">
          <CardContent style={{ padding: "15px" }}>
            <div className="space-y-6">
              {/* Header */}
              <div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">
                  Hold Application
                </h3>
                <p className="text-sm text-gray-600">
                  Manage the processing status of this visa application
                </p>
              </div>

              {/* Hold Status Dropdown */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Processing Status <span className="text-red-500">*</span>
                </label>
                <Select
                  value={holdStatus}
                  disabled={isRefunded || savingHoldStatus}
                  onValueChange={(value: "process" | "hold" | "refunded") =>
                    setHoldStatus(value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select processing status" />
                  </SelectTrigger>
                  <SelectContent>
                    {displayData.isApplicationHold ? (
                      <SelectItem value="process">
                        <div className="flex items-center gap-2">
                          <Play className="w-4 h-4 text-green-600" />
                          <span>Process - Ready to be processed</span>
                        </div>
                      </SelectItem>
                    ) : (
                      <SelectItem value="hold">
                        <div className="flex items-center gap-2">
                          <Ban className="w-4 h-4 text-red-600" />
                          <span>
                            Do Not Process - On hold (can be reactivated)
                          </span>
                        </div>
                      </SelectItem>
                    )}
                    <SelectItem value="refunded">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-blue-600" />
                        <span>Refunded - Permanently closed</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {holdStatus === "process" &&
                    "✓ Application will be processed normally"}
                  {holdStatus === "hold" &&
                    '⚠️ Application is on hold but can be reactivated to "Process" status'}
                  {holdStatus === "refunded" &&
                    "⛔ Application is permanently closed and cannot be processed"}
                </p>
              </div>

              {/* Status Badge */}
              <div
                className={`p-4 rounded-lg border-2 ${
                  holdStatus === "process"
                    ? "bg-green-50 border-green-200"
                    : holdStatus === "hold"
                      ? "bg-amber-50 border-amber-200"
                      : "bg-blue-50 border-blue-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      holdStatus === "process"
                        ? "bg-green-100"
                        : holdStatus === "hold"
                          ? "bg-amber-100"
                          : "bg-blue-100"
                    }`}
                  >
                    {holdStatus === "process" && (
                      <Play className="w-6 h-6 text-green-600" />
                    )}
                    {holdStatus === "hold" && (
                      <Ban className="w-6 h-6 text-amber-600" />
                    )}
                    {holdStatus === "refunded" && (
                      <RefreshCw className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {holdStatus === "process" &&
                        "Application is Ready to Process"}
                      {holdStatus === "hold" && "Application is On Hold"}
                      {holdStatus === "refunded" &&
                        "Application is Refunded & Closed"}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {holdStatus === "process" &&
                        "This application will proceed with normal visa processing workflow"}
                      {holdStatus === "hold" &&
                        'Processing is paused. You can change status back to "Process" anytime'}
                      {holdStatus === "refunded" &&
                        "This application has been refunded and permanently closed"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Remark Textarea */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Remark / Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={holdRemark}
                  disabled={isRefunded || savingHoldStatus}
                  onChange={(e) => setHoldRemark(e.target.value)}
                  placeholder="Please provide a detailed reason for this status change..."
                  className="w-full h-32 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5B9BD5] focus:border-transparent resize-none text-sm"
                />
                <p className="text-xs text-gray-500">
                  Minimum 10 characters required. This will be saved in the
                  application history.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t-2">
                <Button
                  variant="outline"
                  disabled={isRefunded || savingHoldStatus}
                  onClick={() => {
                    setHoldStatus(
                      isRefunded
                        ? "refunded"
                        : displayData.isApplicationHold
                          ? "process"
                          : "hold",
                    );
                    setHoldRemark("");
                    toast.info("Hold settings cleared");
                  }}
                >
                  Reset
                </Button>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isRefunded || savingHoldStatus}
                    onClick={() => {
                      setIsEditing(false);
                      setEditableData(applicant);
                      toast.info("Changes cancelled");
                    }}
                    className="h-9 px-4"
                  >
                    Cancel
                  </Button>
                  <Button
                    style={{ backgroundColor: "#5B9BD5" }}
                    className="text-white"
                    disabled={isRefunded || savingHoldStatus}
                    onClick={saveHoldStatus}
                  >
                    {savingHoldStatus ? (
                      <span className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    {savingHoldStatus ? "Processing..." : "Save Hold Status"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
